import React, { useCallback, useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./components/assets/css/LiveChat.css";
import "./custom.css";

// Shared hooks
import { useLogo, useBalance } from './hooks';
import { sanitizeHtml } from './utils/sanitize';
import { showNotification } from './utils/notify';

// Eager-loaded core components
import Home from './components/pages/mobile/Home';
import APIS from './components/pages/mobile/common/apis';
import GoToTop from './components/common/GoTop';
import Splash from './splash/Splash';
import StoreCode from './StoreCodeComponents';
import Maintenance from './components/pages/mobile/common/Maintenance';
import { Modal } from "react-bootstrap";
import ErrorBoundary from './hooks/ErrorBoundary';

// Lazy-loaded routes for better initial load performance
const Login = lazy(() => import('./components/pages/mobile/Login'));
const SignUp = lazy(() => import('./components/pages/mobile/SignUp'));
const Registion = lazy(() => import('./components/pages/desktop/Registion'));
const MAccount = lazy(() => import('./components/pages/mobile/MAccount'));
const Profile = lazy(() => import('./components/pages/mobile/Profile'));
const MDeposit = lazy(() => import('./components/pages/mobile/MDeposit'));
const MWithdrawal = lazy(() => import('./components/pages/mobile/MWithdrawal'));
const BettingRecord = lazy(() => import('./components/pages/mobile/BettingRecord'));
const TransactionRecord = lazy(() => import('./components/pages/mobile/TransactionRecord'));
const TurnoverRecord = lazy(() => import('./components/pages/mobile/TurnoverRecord'));
const RefrrelProgram = lazy(() => import('./components/pages/mobile/RefrrelProgram'));
const ResetPassword = lazy(() => import('./components/pages/mobile/ResetPassword'));
const MobileInbox = lazy(() => import('./components/pages/mobile/MobileInbox'));
const VIPConvert = lazy(() => import('./components/pages/mobile/VIPConvert'));
const Games = lazy(() => import('./components/pages/mobile/Games'));
const GameRun = lazy(() => import('./components/pages/mobile/GameRun'));
const MobilePromotion = lazy(() => import('./components/pages/mobile/MobilePromotion'));
const ChatHome = lazy(() => import('./components/Chat/ChatHome'));
const ChatHistory = lazy(() => import('./components/Chat/ChatHistory'));
const ChatBox = lazy(() => import('./components/Chat/ChatBox'));
const EXHome = lazy(() => import('./components/exchanges/EXHome'));
const EXHomeVelki = lazy(() => import('./components/exchanges/EXHomeVelki'));
const MatchOdds = lazy(() => import('./components/exchanges/MatchOdds'));
const ExAccount = lazy(() => import('./components/exchanges/ExAccount'));
const InPlay = lazy(() => import('./components/exchanges/InPlay'));
const PaymentTimer = lazy(() => import('./components/pages/mobile/common/PaymentTimer'));
const ForgotPassword = lazy(() => import('./components/pages/mobile/ForgotPassword'));
const MakePayment = lazy(() => import('./components/pages/mobile/common/MakePayment'));
const FreeSpinPage = lazy(() => import('./free-spin/FreeSpinPage'));
const NineWiket = lazy(() => import('./components/pages/mobile/NineWiket'));

// Static styles outside component to avoid re-creation
const LOADING_FALLBACK = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#111' }}>
    <div className="three-dots-loading">
      <span></span><span></span><span></span>
    </div>
  </div>
);

function App() {
	const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
	const [showContent, setShowContent] = useState(false);
	const location = useLocation();

	// ── /#/9wkt exit handler ──────────────────────────────────────────────
	useEffect(() => {
		if (window.location.href.includes('/#/9wkt')) {
			const token = localStorage.getItem('token');
			fetch(`${process.env.REACT_APP_API_URL}/api/9wkt/exit`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			}).finally(() => {
				const cleanUrl = window.location.href.split('/#/9wkt')[0];
				window.location.replace(cleanUrl);
			});
		}
	}, []);

	const [globalData, setGlobalData] = useState({});
	const [showModal, setShowModal] = useState(false);
	const [modalMessage, setModalMessage] = useState('');
	const [modalType, setModalType] = useState('error');
	const [refreshPvtData, setRefreshPvtData] = useState(false);
	const [gtmId, setGtmId] = useState(null);
	const [cssLoading, setCssLoading] = useState(false);

	// ── Use shared hooks (deduplicates API calls) ─────────────────────────
	const { favicon, logo: pwaUrl, siteName, loading: logoLoading } = useLogo();
	const { balance, fetchBalance } = useBalance();

	// ── Set favicon and PWA icon from shared logo hook ────────────────────
	useEffect(() => {
		if (favicon) {
			let link = document.querySelector("link[rel='icon']");
			if (!link) {
				link = document.createElement("link");
				link.rel = "icon";
				document.head.appendChild(link);
			}
			link.href = favicon;
		}
	}, [favicon]);

	// ── Show alert messages ──────────────────────────────────────────────
	const showAlert = useCallback((message = false, type = 'error') => {
		if (!message) {
			setShowModal(false);
			setModalMessage('');
			return;
		}
		setShowModal(true);
		setModalType(type || 'error');
		setModalMessage(message);
	}, []);

	// ── Listen for global notifications (for components without showAlert prop) ──
	useEffect(() => {
		const handleGlobalNotification = (event) => {
			const { message, type } = event.detail || {};
			if (message) {
				showAlert(message, type || 'info');
			}
		};
		window.addEventListener('global-notification', handleGlobalNotification);
		return () => window.removeEventListener('global-notification', handleGlobalNotification);
	}, [showAlert]);

	// ── CSS lazy loading ─────────────────────────────────────────────────
	useEffect(() => {
		let isMounted = true;
		let extraCssLink = null;

		const loadCss = async () => {
			setCssLoading(true);
			try {
				const promises = [
					import("./components/assets/css/flipclock.css"),
					import("./components/assets/css/Mobile.css"),
				];
				if (location.pathname === "/make-payment") {
					promises.push(new Promise((resolve, reject) => {
						extraCssLink = document.createElement("link");
						extraCssLink.rel = "stylesheet";
						extraCssLink.href = "./extra.css";
						extraCssLink.id = "extra-css";
						extraCssLink.onload = resolve;
						extraCssLink.onerror = reject;
						document.head.appendChild(extraCssLink);
					}));
				}
				await Promise.all(promises);
			} catch (err) {
				// CSS load failure is non-critical
			} finally {
				if (isMounted) setCssLoading(false);
			}
		};

		loadCss();

		return () => {
			isMounted = false;
			if (extraCssLink) extraCssLink.remove();
			const existing = document.getElementById("extra-css");
			if (existing) existing.remove();
		};
	}, [location.pathname]);

	// ── GTM (Google Tag Manager) ─────────────────────────────────────────
	useEffect(() => {
		const id = globalData?.general?.google_tag;
		if (!id) return;
		setGtmId(id);

		if (typeof window !== "undefined") {
			window.dataLayer = window.dataLayer || [];
		}

		const oldScript = document.getElementById("gtm-script");
		if (oldScript) oldScript.remove();

		const script = document.createElement("script");
		script.id = "gtm-script";
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
		document.head.appendChild(script);

		window.dataLayer.push({
			"gtm.start": new Date().getTime(),
			event: "gtm.js",
		});

		return () => {
			const s = document.getElementById("gtm-script");
			if (s) s.remove();
		};
	}, [globalData?.general?.google_tag]);

	// ── PWA manifest update ──────────────────────────────────────────────
	useEffect(() => {
		const name = siteName || globalData?.general?.site_title;
		if (!name && !pwaUrl) return;

		const manifestData = {
			short_name: name || document.title,
			name: name || document.title,
			start_url: '/',
			display: 'standalone',
			theme_color: '#000000',
			background_color: '#ffffff',
			icons: pwaUrl
				? [
					{ src: pwaUrl, sizes: '192x192', type: 'image/png' },
					{ src: pwaUrl, sizes: '512x512', type: 'image/png' },
				]
				: [],
		};

		const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/json' });
		const manifestUrl = URL.createObjectURL(blob);
		let manifestLink = document.querySelector("link[rel='manifest']");
		if (!manifestLink) {
			manifestLink = document.createElement('link');
			manifestLink.rel = 'manifest';
			document.head.appendChild(manifestLink);
		}
		manifestLink.href = manifestUrl;
		return () => URL.revokeObjectURL(manifestUrl);
	}, [siteName, pwaUrl, globalData?.general?.site_title]);

	// ── Auth helpers ─────────────────────────────────────────────────────
	const handleLogout = useCallback(() => {
		localStorage.clear();
		setIsLoggedIn(false);
		window.location.href = '/';
	}, []);

	// ── PrivateRoute (memoized) ─────────────────────────────────────────
	const PrivateRoute = useCallback(({ element }) =>
		isLoggedIn ? element : <Navigate to="/login" />,
	[isLoggedIn]);

	// ── Memoized globalData-derived values ──────────────────────────────
	const general = globalData?.general || {};
	const userData = globalData?.userData || [];
	const transactions = globalData?.transactions || [];
	const paymentMethods = globalData?.paymentMethods || [];
	const promotions = globalData?.promotions || {};
	const sliders = globalData?.sliders || {};
	const marketBlock = globalData?.marketBlock || [];
	const turnovers = globalData?.turnovers || [];
	const hasEWallet = globalData?.hasEWallet || false;
	const socialLinks = globalData?.socialLinks || {};

	return (
		<ErrorBoundary>
			<div className="App">
				{gtmId && (
					<noscript>
						<iframe
							src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
							height="0"
							width="0"
							style={{ display: "none", visibility: "hidden" }}
							title="gtm"
						/>
					</noscript>
				)}
				<GoToTop />
				<APIS setGlobalData={setGlobalData} refreshPvtData={refreshPvtData} />

				{/* Splash screen on initial load */}
				<Splash onSplashComplete={() => {
					setShowContent(true);
					setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
				}} />

				{/* CSS loading fallback */}
				{cssLoading && <Splash />}

				{/* Main app content */}
				{showContent && (
					<Suspense fallback={LOADING_FALLBACK}>
						<Routes>
							<Route
								path="/"
								element={
									general?.site_status === 0
										? <Maintenance />
										: <Home globalData={globalData} />
								}
							/>
							<Route path="/signup" element={<SignUp slidersMain={sliders?.main || []} />} />
							<Route path="/login" element={<Login />} />
							<Route path="/games" element={<Games />} />
							<Route path="/forgot-password" element={<ForgotPassword showAlert={showAlert} />} />
							<Route
								path="/account"
								element={
									<PrivateRoute
										element={
											<MAccount
												fetchBalance={fetchBalance}
												userData={userData}
												general={general}
												sociallink={socialLinks}
											/>
										}
									/>
								}
							/>
							<Route path="/9wiket" element={<NineWiket />} />
							<Route path="/profile" element={<PrivateRoute element={<Profile />} />} />
							<Route path="/deposit" element={<PrivateRoute element={<MDeposit usdtRate={general?.usdt_rate} hasEWallet={hasEWallet} userData={userData} transactionData={transactions} items={paymentMethods} promotions={promotions?.activePromotion || []} />} />} />
							<Route path="/withdrawal" element={<PrivateRoute element={<MWithdrawal general={general} hasEWallet={hasEWallet} userData={userData} transactionData={transactions} items={paymentMethods} />} />} />
							<Route path="/betting-record" element={<PrivateRoute element={<BettingRecord />} />} />
							<Route path="/transaction-record" element={<PrivateRoute element={<TransactionRecord setRefreshPvtData={setRefreshPvtData} transactionData={transactions} />} />} />
							<Route path="/turnover-record" element={<PrivateRoute element={<TurnoverRecord turnovers={turnovers} />} />} />
							<Route path="/refrrel-program" element={<PrivateRoute element={<RefrrelProgram showAlert={showAlert} userData={userData} />} />} />
							<Route path="/reset-password" element={<PrivateRoute element={<ResetPassword showAlert={showAlert} />} />} />
							<Route path="/inbox" element={<PrivateRoute element={<MobileInbox />} />} />
							<Route path="/vip" element={<PrivateRoute element={<VIPConvert />} />} />
							<Route path="/gamerun" element={<PrivateRoute element={<GameRun />} />} />
							<Route path="/promotions" element={<PrivateRoute element={<MobilePromotion promotions={promotions?.allPromotions || []} />} />} />
							<Route path="/chat" element={<PrivateRoute element={<ChatHome />} />} />
							<Route path="/chat-list" element={<PrivateRoute element={<ChatHistory />} />} />
							<Route path="/chat-box/:chatId" element={<PrivateRoute element={<ChatBox />} />} />
							<Route path="/odds-view" element={<PrivateRoute element={<MatchOdds />} />} />
							<Route path="/exchange" element={<PrivateRoute element={<EXHome />} />} />
							<Route path="/velki" element={<PrivateRoute element={<EXHomeVelki />} />} />
							<Route path="/exchange-account" element={<PrivateRoute element={<ExAccount />} />} />
							<Route path="/exchange-inplay" element={<PrivateRoute element={<InPlay />} />} />
							<Route path="/payment-request" element={<PrivateRoute element={<PaymentTimer />} />} />
							<Route path="/referral_code" element={<StoreCode codeType="referral_code" />} />
							<Route path="/affiliate_code" element={<StoreCode codeType="affiliate_code" />} />
							<Route path="/agent_code" element={<StoreCode codeType="agent_code" />} />
							<Route path="/make-payment" element={<MakePayment hasEWallet={hasEWallet} userData={userData} />} />
							<Route path="/free-spin" element={<FreeSpinPage />} />
							<Route path="/register" element={<Registion />} />
							{/* Catch-all: redirect unknown routes to home */}
							<Route path="*" element={<Navigate to="/" replace />} />
						</Routes>
					</Suspense>
				)}

				{/* Global Modal (preserves original behavior) */}
				{showContent && (
					<Modal show={showModal} onHide={() => setShowModal(false)} centered>
						<Modal.Header closeButton>
							<Modal.Title>
								{modalType === 'success' ? 'Success' : modalType === 'info' ? 'Notice' : 'Error'}
							</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(modalMessage) }} />
						</Modal.Body>
					</Modal>
				)}
			</div>
		</ErrorBoundary>
	);
}

export default App;
